/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  listAuditLogsPage,
  countAuditLogs,
  type AuditLogRow,
  type ListAuditLogsPageOptions,
} from '../../shell/audit/auditLogs';
import { groupAuditRowsByDay } from '../../shell/audit/auditLogPresentation';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { resolveActivityHistoryOnlyCurrentUser } from './profileScreen.utils';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { usePaginatedRemoteList } from '../../shared/data/usePaginatedRemoteList';
import { CatalogPagination } from '../../shared/ui/CatalogPagination';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { settingsStyles } from './settingsScreen.styles';
import { SettingsAuditListScreen } from './components/SettingsAuditListScreen';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../../team/store/useEmployeesStore';
import { buildAuthUserLabelById, resolveAuthUserDisplayLabel } from '../../auth/domain/authUserDisplayLabels';
import { useTeamHistoryFilters } from '../../team/screens/history/useTeamHistoryFilters';
import { TeamHistoryFiltersPanel } from '../../team/screens/history/TeamHistoryFiltersPanel';
import { TeamHistoryTable } from '../../team/screens/history/TeamHistoryTable';
import { buildTeamHistoryCsv } from '../../team/screens/history/teamHistoryExport';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { ticketColors } from '../../shared/theme/ticketColors';

const ACTIVITY_PAGE_SIZE_STORAGE_KEY = 'vuelto.audit.activity_history.pageSize';

type ActivityQuery = {
  options: ListAuditLogsPageOptions;
};

export const ActivityHistoryScreen = () => {
  const { entitlements, loading: entLoading } = usePlanEntitlements();
  const onlyCurrentUser = resolveActivityHistoryOnlyCurrentUser(entitlements?.is_owner);

  const [selectedRow, setSelectedRow] = useState<AuditLogRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const filters = useTeamHistoryFilters('7d');

  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const ownerAuthUserId = sessionUser?.id ?? null;
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const labelById = useMemo(
    () =>
      buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId,
        employees,
      }),
    [sessionUser, ownerAuthUserId, employees],
  );

  const resolveUserLabel = useCallback(
    (userId: string | null) => resolveAuthUserDisplayLabel(userId, labelById, ownerAuthUserId),
    [labelById, ownerAuthUserId],
  );

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const [id, name] of Object.entries(labelById)) {
      seen.set(id, name);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [labelById]);

  const query = useMemo<ActivityQuery>(
    () => ({ options: { onlyCurrentUser } }),
    [onlyCurrentUser],
  );

  const criteriaKey = `onlyCurrentUser:${onlyCurrentUser}`;

  const enabled = !entLoading;

  const paging = usePaginatedRemoteList<AuditLogRow, ActivityQuery>({
    storageKey: ACTIVITY_PAGE_SIZE_STORAGE_KEY,
    defaultPageSize: 25,
    criteriaKey,
    query,
    fetchPage: ({ limit, offset, query: q }) => listAuditLogsPage(offset, q.options, limit),
    count: (q: ActivityQuery) => countAuditLogs(q.options),
    enabled,
    logLabel: 'ActivityHistory',
  });

  const rows = paging.items;
  const loading = paging.loading;
  const hasMore = paging.hasMore;
  const totalCount = paging.totalCount;
  const pageIndex = paging.pageIndex;
  const pageSize = paging.pageSize;
  const setPageSize = paging.setPageSize;
  const loadNextPage = paging.loadNextPage;
  const loadPreviousPage = paging.loadPreviousPage;
  const pagingError = paging.error;
  const reloadFromStart = paging.reloadFromStart;

  const filteredRows = useMemo(() => filters.filteredRows(rows), [filters, rows]);
  const sections = useMemo(() => groupAuditRowsByDay(filteredRows), [filteredRows]);

  const { refreshing, onRefresh } = useScreenLoadShell('ActivityHistory', async (opts) => {
    if (opts?.force) await reloadFromStart();
  });

  const handleExportCsv = useCallback(async () => {
    if (filteredRows.length === 0) {
      showInfo('Exportar', 'No hay eventos con estos filtros.');
      return;
    }
    try {
      const csv = buildTeamHistoryCsv(filteredRows, resolveUserLabel);
      const filename = `actividad_cuenta_${new Date().toISOString().slice(0, 10)}.csv`;
      const outcome = await shareOrDownloadCsv(csv, filename);
      if (outcome === 'ok') {
        showSuccess('CSV descargado', filename);
      }
    } catch (e) {
      console.warn('[ActivityHistoryScreen] export csv', e);
      const msg = e instanceof Error ? e.message : 'No se pudo generar el archivo.';
      showError('No se pudo exportar', msg);
    }
  }, [filteredRows, resolveUserLabel]);

  const { layoutWebShell } = useContentLayout('settingsWide');

  const headerExtra = (
    <TeamHistoryFiltersPanel filters={filters} userOptions={userOptions} showUserFilter={false} />
  );

  const heroActions = (
    <TouchableOpacity
      style={styles.exportBtn}
      onPress={() => void handleExportCsv()}
      accessibilityRole="button"
      accessibilityLabel="Exportar actividad a CSV"
    >
      <Ionicons name="download-outline" size={16} color={ticketColors.ink} />
      <Text style={styles.exportBtnText}>Exportar CSV</Text>
    </TouchableOpacity>
  );

  const paginationProps = {
    catalogPageIndex: pageIndex,
    pageSize,
    catalogTotalCount: totalCount,
    hasMore,
    productsLoading: loading,
    productCount: filteredRows.length,
    onPreviousPage: loadPreviousPage,
    onNextPage: loadNextPage,
    listNouns: { singular: 'evento', plural: 'eventos' } as const,
    alwaysShowPagination: true as const,
    pageSizePicker: {
      label: 'Eventos por página',
      options: [10, 25, 50] as const,
      value: (pageSize <= 50 ? pageSize : 50) as 10 | 25 | 50,
      onChange: setPageSize as (size: number) => void,
      accessibilityLabelPrefix: 'Eventos por página',
    },
  };

  const desktopTable = layoutWebShell ? (
    <View style={styles.tableWrap}>
      <TeamHistoryTable
        rows={filteredRows}
        resolveUserLabel={resolveUserLabel}
      />
      <CatalogPagination {...paginationProps} />
    </View>
  ) : undefined;

  const lead = entLoading && !entitlements
    ? 'Cargando tu actividad…'
    : entitlements?.is_owner === false
      ? 'Lo que hiciste vos: sesión, ventas, stock y más.'
      : 'Lo que hiciste vos y tu equipo: sesión, ventas, stock, clientes, catálogo y más.';

  return (
    <SettingsAuditListScreen
      lead={lead}
      errorMessage={pagingError ?? loadError}
      sections={sections}
      loading={loading || entLoading}
      refreshing={refreshing}
      onRefresh={() => void onRefresh()}
      loadingMore={false}
      hasMore={false}
      onLoadMore={() => {}}
      emptyLabel="Todavía no hay actividad."
      selectedRow={selectedRow}
      onSelectRow={setSelectedRow}
      headerExtra={headerExtra}
      heroActions={heroActions}
      desktopTable={desktopTable}
      resolveUserLabel={resolveUserLabel}
      customFooter={!layoutWebShell ? <CatalogPagination {...paginationProps} /> : null}
      mobile={!layoutWebShell}
    />
  );
};

const styles = StyleSheet.create({
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  exportBtnText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  tableWrap: {
    width: '100%',
    alignItems: 'center',
  },
});
